import { motion } from 'framer-motion';
import { Clock, XCircle, Ban, HelpCircle, ArrowLeft } from 'lucide-react';
import type { IncidentType } from '../types';
import { incidentTypes, springConfig, type ClaimTheme } from '../constants';
import { cn } from '@/lib/utils';

interface IncidentSelectSectionProps {
  selectedType: IncidentType | null;
  onSelect: (type: IncidentType) => void;
  onBack: () => void;
  theme?: ClaimTheme;
}

const icons = {
  delay: Clock,
  cancel: XCircle,
  denied_boarding: Ban,
  other: HelpCircle,
};

const colors = {
  delay: {
    bg: 'bg-amber-50 hover:bg-amber-100',
    border: 'border-amber-200',
    icon: 'text-amber-500',
    selected: 'border-amber-400 bg-amber-50 ring-2 ring-amber-400/20',
  },
  cancel: {
    bg: 'bg-red-50 hover:bg-red-100',
    border: 'border-red-200',
    icon: 'text-red-500',
    selected: 'border-red-400 bg-red-50 ring-2 ring-red-400/20',
  },
  denied_boarding: {
    bg: 'bg-orange-50 hover:bg-orange-100',
    border: 'border-orange-200',
    icon: 'text-orange-500',
    selected: 'border-orange-400 bg-orange-50 ring-2 ring-orange-400/20',
  },
  other: {
    bg: 'bg-slate-50 hover:bg-slate-100',
    border: 'border-slate-200',
    icon: 'text-slate-500',
    selected: 'border-slate-400 bg-slate-50 ring-2 ring-slate-400/20',
  },
};

export function IncidentSelectSection({
  selectedType,
  onSelect,
  onBack,
  theme = 'invol',
}: IncidentSelectSectionProps) {
  // Theme-aware checkmark color
  const checkmarkBg = theme === 'drboese' ? 'bg-[rgb(255,211,52)]' : 'bg-blue-500';
  const checkmarkText = theme === 'drboese' ? 'text-black' : 'text-white';
  return (
    <motion.div
      key="incident-select"
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col"
    >
      {/* Back button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onBack}
        className="self-start -ml-2 mb-4 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
      </motion.button>

      {/* Header */}
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-3xl font-bold text-slate-900 mb-2"
      >
        Was ist mit Ihrem Flug passiert?
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="text-slate-500 mb-8"
      >
        Wählen Sie die Art der Störung aus, die Sie erlebt haben
      </motion.p>

      {/* Incident type grid */}
      <div className="grid grid-cols-2 gap-4">
        {incidentTypes.map((type, index) => {
          const Icon = icons[type.id];
          const colorConfig = colors[type.id];
          const isSelected = selectedType === type.id;

          return (
            <motion.button
              key={type.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                ...springConfig.default,
                delay: 0.2 + index * 0.05,
              }}
              onClick={() => onSelect(type.id)}
              className={cn(
                'relative p-5 rounded-xl border-2 text-left transition-all',
                isSelected ? colorConfig.selected : `${colorConfig.bg} ${colorConfig.border}`
              )}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon className={cn('w-8 h-8 mb-3', colorConfig.icon)} />
              <p className="font-semibold text-slate-800 text-base">
                {type.label}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {type.description}
              </p>

              {/* Selection checkmark */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className={cn('absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center', checkmarkBg)}
                >
                  <svg
                    className={cn('w-4 h-4', checkmarkText)}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}
